// Shared design tokens for React Flow custom nodes.
// Keeps every node visually consistent: same shell (radius, shadow, border),
// a per-type accent colour, and small helpers for the icon "chip" and the
// selected-ring glow.

export const NODE_RADIUS = 9;
export const NODE_INNER_RADIUS = NODE_RADIUS - 1; // sits inside the 1px shell border
export const NODE_WIDTH = 216;

// Soft, layered elevation – reads as "floating card" rather than a hard drop shadow.
export const NODE_SHADOW =
    "0 1px 2px rgba(16, 24, 40, 0.06), 0 6px 16px -6px rgba(16, 24, 40, 0.14)";

export const NODE_SHADOW_HOVER =
    "0 2px 4px rgba(16, 24, 40, 0.08), 0 12px 28px -8px rgba(16, 24, 40, 0.22)";

// Per-type palette. `accent` drives header text / icon / handles / selected ring,
// `soft` is the tinted header background, `body` is the content area.
export const NODE_PALETTE = {
    custom: {accent: "#2F7BFF", soft: "#EAF2FF", border: "#D3E4FF", body: "#FFFFFF", onAccent: "#FFFFFF"},
    flow: {accent: "#2F7BFF", soft: "#EAF2FF", border: "#D3E4FF", body: "#FFFFFF", onAccent: "#FFFFFF"},
    intent: {accent: "#2563EB", soft: "#EAF1FF", border: "#D6E4FF", body: "#FFFFFF", onAccent: "#FFFFFF"},
    form: {accent: "#7C3AED", soft: "#F3EEFF", border: "#E7DBFF", body: "#FFFFFF", onAccent: "#FFFFFF"},
    block: {accent: "#475569", soft: "#F1F5F9", border: "#E2E8F0", body: "#FFFFFF", onAccent: "#FFFFFF"},
    transfer: {accent: "#0E9F9A", soft: "#E4FBF8", border: "#C7F2ED", body: "#FFFFFF", onAccent: "#FFFFFF"},
    start: {accent: "#059669", soft: "#059669", border: "#047857", body: "#059669", onAccent: "#FFFFFF"},
    end: {accent: "#1E293B", soft: "#1E293B", border: "#0F172A", body: "#1E293B", onAccent: "#FFFFFF"},
    // shown when a node has no outgoing route yet
    warning: {accent: "#D97706", soft: "#FEF6E7", border: "#FBE4BE", body: "#FFFFFF", onAccent: "#FFFFFF"},
};

export const getPalette = (key) => NODE_PALETTE[key] || NODE_PALETTE.custom;

// Base shell applied to every node <Box>. NOTE: no `overflow: hidden` here –
// the connection handles are rendered as children of the shell and sit on the
// border, so clipping the shell would cut them in half. The rounded corners are
// clipped by the inner surface wrapper (`nodeSurfaceSx`) instead.
export const nodeShellSx = (palette, {selected = false} = {}) => ({
    position: "relative",
    width: "100%",
    borderRadius: `${NODE_RADIUS}px`,
    border: "1px solid",
    // a touch more defined than the pale divider colour, but still quiet
    borderColor: hexToRgba(palette.accent, 0.3),
    background: palette.body,
    boxShadow: NODE_SHADOW,
    transition: "box-shadow .18s ease, border-color .18s ease, transform .18s ease",
    "&:hover": {boxShadow: NODE_SHADOW_HOVER},
    ...(selected && {
        borderColor: palette.accent,
        boxShadow: `${NODE_SHADOW}, 0 0 0 3px ${hexToRgba(palette.accent, 0.22)}`,
    }),
});

// Wraps the visible content (header + body). No `overflow: hidden` – that would
// clip the connection handles that straddle the border. Rounded corners are
// applied per-section via `sectionSx` instead.
export const nodeSurfaceSx = {
    borderRadius: `${NODE_INNER_RADIUS}px`,
};

// Rounds only the corners a stacked section actually exposes on the card edge:
// `first` -> top corners, `last` -> bottom corners.
export const sectionSx = ({first = false, last = false} = {}) => ({
    ...(first && {
        borderTopLeftRadius: `${NODE_INNER_RADIUS}px`,
        borderTopRightRadius: `${NODE_INNER_RADIUS}px`,
    }),
    ...(last && {
        borderBottomLeftRadius: `${NODE_INNER_RADIUS}px`,
        borderBottomRightRadius: `${NODE_INNER_RADIUS}px`,
    }),
});

// Tinted header strip that carries the icon chip + title.
export const nodeHeaderSx = (palette, {solid = false} = {}) => ({
    display: "flex",
    alignItems: "center",
    gap: 0.75,
    px: 1,
    py: 0.75,
    background: solid ? palette.body : palette.soft,
    color: solid ? palette.onAccent : "#1E293B",
    borderBottom: solid ? "none" : `1px solid ${palette.border}`,
});

// Rounded square that holds the node's icon.
export const iconChipSx = (palette, {solid = false} = {}) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: 20,
    height: 20,
    borderRadius: "6px",
    color: solid ? palette.onAccent : palette.accent,
    background: solid ? hexToRgba("#FFFFFF", 0.18) : hexToRgba(palette.accent, 0.12),
});

export const nodeTitleSx = (solid = false) => ({
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: "0.01em",
    color: solid ? "inherit" : "#1E293B",
    lineHeight: 1.25,
});

export const nodeKickerSx = (palette) => ({
    display: "block",
    fontSize: 7.5,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: palette.accent,
    lineHeight: 1.4,
});

export function hexToRgba(hex, alpha) {
    const v = hex.replace("#", "");
    const r = parseInt(v.substring(0, 2), 16);
    const g = parseInt(v.substring(2, 4), 16);
    const b = parseInt(v.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
